import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client/react';

import { requestDownloadFiles } from '@atom/actions/fileActions';
import MediaTableHeader from '@atom/components/common/mediaTable/MediaTableHeader';
import WorkTemplateContext, {
  WorkTemplateActionTypes,
} from '@atom/components/workTemplate/WorkTemplateContext';
import textConstants from '@atom/constants/textConstants';
import {
  GET_MEDIA,
  MEDIA_BULK_DELETE,
  MEDIA_DELETE,
  MEDIA_UPDATE,
} from '@atom/graph/media';
import { WORK_TEMPLATE_UPDATE } from '@atom/graph/workTemplate';
import { useFileUpload } from '@atom/hooks/useFileUpload';
import { Snackbar } from '@atom/mui';
import { Media, MediaType, SubjectType } from '@atom/types/media';
import {
  InheritedComponentType,
  WorkTemplateMedia,
} from '@atom/types/workTemplate';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import { stripTypename } from '@atom/utilities/typenameUtilities';
import { isComponentInherited } from '@atom/utilities/workTemplateUtilities';

import VideosPagination from './VideosPagination';
import VideoTile from './VideoTile';

import classes from './workTemplateVideos.module.css';

const PAGE_LIMIT = 12;

const WorkTemplateVideos = () => {
  const reduxDispatch = useDispatch();
  const [uploadFiles] = useFileUpload();
  const { workTemplate, dispatch } = useContext(WorkTemplateContext);

  const [media, setMedia] = useState<Media[]>([]);
  const [mediaTotal, setMediaTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set([]));

  const isInherited = isComponentInherited(
    InheritedComponentType.MEDIA,
    workTemplate?.inheritedComponents || [],
  );

  const parentSubjectIds = isInherited
    ? [workTemplate.id, workTemplate.parentId]
    : [workTemplate.id];

  const {
    data: mediaData,
    refetch,
    loading: loadingMedia,
  } = useQuery(GET_MEDIA, {
    variables: {
      input: {
        parentSubjectIds,
        type: MediaType.VIDEO,
        page,
        limit: PAGE_LIMIT,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (mediaData?.media) {
      setMedia(mediaData?.media?.media);
      setMediaTotal(mediaData?.media?.totalCount);
    }
  }, [mediaData]);

  const [workTemplateUpdate] = useMutation(WORK_TEMPLATE_UPDATE);

  const [mediaUpdate, { loading: loadingUpdate }] = useMutation(MEDIA_UPDATE);

  const [mediaDelete, { loading: loadingDelete }] = useMutation(MEDIA_DELETE);

  const [mediaBulkDelete, { loading: loadingBulkDelete }] =
    useMutation(MEDIA_BULK_DELETE);

  const updateWorkTemplateMedia = async (updatedMedia: WorkTemplateMedia[]) => {
    const { data } = await workTemplateUpdate({
      variables: {
        input: {
          workOrderTemplateId: workTemplate.id,
          media: updatedMedia,
        },
      },
    });

    dispatch({
      type: WorkTemplateActionTypes.UPDATE_WORK_TEMPLATE_PROPERTY,
      data: {
        property: 'media',
        value: data?.workOrderTemplateUpdate?.media,
      },
    });
  };

  const resetToFirstPage = () => {
    if (page === 1) {
      refetch({
        input: {
          parentSubjectIds,
          type: MediaType.VIDEO,
          page,
          limit: PAGE_LIMIT,
        },
      });
    } else {
      setPage(1);
    }
  };

  const handleUpload = async event => {
    const { files } = event.target;

    if (!files.length) {
      return;
    }

    const uploadedMedia = await uploadFiles({
      files,
      subjectId: workTemplate.id,
      parentSubjectId: workTemplate.id,
      subjectType: SubjectType.WORK_ORDER_TEMPLATE,
    });

    await updateWorkTemplateMedia([
      ...workTemplate?.media.map(item => stripTypename(item)),
      ...uploadedMedia.map(medium => ({
        fileId: medium.fileId,
        name: medium.name,
        type: medium.type,
        fileExtension: medium.fileExtension,
        mediaId: medium.id,
      })),
    ]);

    refetch();
  };

  const renameMedia = async (id: string, name: string) => {
    const res = await mediaUpdate({
      variables: {
        input: {
          id,
          name,
        },
      },
    });

    const updatedMedia = media.map((mediaItem: Media) => {
      return mediaItem.id === id ? res?.data?.mediaUpdate : mediaItem;
    });

    const otherMedia = workTemplate?.media
      .filter(item => item.type !== MediaType.VIDEO)
      .map(item => stripTypename(item));

    await updateWorkTemplateMedia([
      ...otherMedia,
      ...updatedMedia.map(medium => ({
        fileId: medium.fileId,
        name: medium.name,
        type: medium.type,
        fileExtension: medium.fileExtension,
        mediaId: medium.id,
      })),
    ]);

    setMedia(updatedMedia);
  };

  const deleteMedia = async (id: string) => {
    try {
      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_DELETING_TEXT,
      });

      await mediaDelete({
        variables: {
          id,
        },
      });

      Snackbar.info({
        message: 'Successfully deleted file',
      });

      await updateWorkTemplateMedia(
        workTemplate.media
          .filter(item => item.mediaId !== id)
          .map(item => stripTypename(item)),
      );

      setSelectedItems(new Set([]));
      resetToFirstPage();
    } catch (err) {
      Snackbar.error({
        message: 'Failed to delete file',
      });
    }
  };

  const downloadMultipleFiles = async () => {
    const selectedMedia = media.filter((medium: any) =>
      selectedItems.has(medium.id),
    );

    reduxDispatch(requestDownloadFiles(selectedMedia));
  };

  const deleteMultipleFiles = async () => {
    await mediaBulkDelete({
      variables: {
        input: {
          ids: [...selectedItems],
        },
      },
    });

    await updateWorkTemplateMedia(
      workTemplate.media
        .filter(item => !selectedItems.has(item.mediaId))
        .map(item => stripTypename(item)),
    );

    setSelectedItems(new Set([]));
    resetToFirstPage();
  };

  const incrementPage = (increment: number) => {
    setPage(page + increment);
    setSelectedItems(new Set([]));
  };

  const toggleCheckbox = (id: string) => {
    setSelectedItems(toggleFromSet(selectedItems, id));
  };

  const isEditDisabled =
    workTemplate?.published || doesNotHaveRolePermissions(ROLE_SETS.ORG_ADMIN);

  const loading =
    loadingMedia || loadingUpdate || loadingDelete || loadingBulkDelete;

  return (
    <div className={classes.bodyContainer}>
      <div className={classes.centerPane}>
        <MediaTableHeader
          type={MediaType.VIDEO}
          totalCount={mediaTotal}
          selectedItems={selectedItems}
          isEditDisabled={isEditDisabled}
          isInherited={isInherited}
          handleUpload={handleUpload}
          handleDownloadMultipleFiles={downloadMultipleFiles}
          handleDeleteMultipleMedia={deleteMultipleFiles}
          loading={loading}
        />
        <div className={classes.photoBlock}>
          <div className={classes.photoTilesContainer}>
            {media.map((medium: Media) => {
              const isChecked = selectedItems.has(medium.id);

              const isItemInherited = isComponentInherited(
                InheritedComponentType.MEDIA,
                workTemplate?.inheritedComponents || [],
                medium?.fileId,
              );

              return (
                <VideoTile
                  key={medium.id}
                  media={medium}
                  renameMedia={renameMedia}
                  deleteMedia={deleteMedia}
                  loading={loading}
                  isEditDisabled={isEditDisabled}
                  isChecked={isChecked}
                  toggleCheckbox={toggleCheckbox}
                  isItemInherited={isItemInherited}
                />
              );
            })}
          </div>
        </div>
        {!!media.length && (
          <VideosPagination
            incrementPage={incrementPage}
            page={page}
            limit={PAGE_LIMIT}
            total={mediaTotal}
          />
        )}
      </div>
    </div>
  );
};

export default WorkTemplateVideos;
